import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const AnnouncementsTab = ({
  announcementForm,
  announcementImageRef,
  announcements,
  customers,
  deleteAnnouncement,
  editingAnnouncement,
  handleAnnouncementSubmit,
  isAnnouncementDialogOpen,
  openEditAnnouncementDialog,
  promotions,
  resetAnnouncementForm,
  setAnnouncementForm,
  setIsAnnouncementDialogOpen,
}) => (
          <TabsContent value="announcements">
            <div className="space-y-6">
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Megaphone className="h-5 w-5 text-[#FFC107]" />
                    Homepage Announcements
                  </CardTitle>
                  <Dialog open={isAnnouncementDialogOpen} onOpenChange={setIsAnnouncementDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                        onClick={resetAnnouncementForm}
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141417] border-zinc-700 max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-white">
                          {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Title</Label>
                          <Input
                            value={announcementForm.title}
                            onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                            placeholder="e.g., Free Delivery!"
                            className="bg-[#1C1C22] border-zinc-700 text-white"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Message</Label>
                          <Textarea
                            value={announcementForm.message}
                            onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                            placeholder="Enter the announcement message..."
                            className="bg-[#1C1C22] border-zinc-700 text-white"
                            rows={3}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Type</Label>
                          <Select
                            value={announcementForm.type}
                            onValueChange={(value) => setAnnouncementForm({...announcementForm, type: value})}
                          >
                            <SelectTrigger className="bg-[#1C1C22] border-zinc-700 text-white">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1C1C22] border-zinc-700">
                              <SelectItem value="info">Info (Blue)</SelectItem>
                              <SelectItem value="success">Success (Green)</SelectItem>
                              <SelectItem value="warning">Warning (Yellow)</SelectItem>
                              <SelectItem value="promo">Promo (Gold)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Attachment Image (Optional)</Label>
                          <div className="flex gap-2">
                            <Input
                              value={announcementForm.image}
                              onChange={(e) => setAnnouncementForm({...announcementForm, image: e.target.value})}
                              placeholder="Image URL or upload"
                              className="bg-[#1C1C22] border-zinc-700 text-white flex-1"
                            />
                            <input
                              type="file"
                              ref={announcementImageRef}
                              className="hidden"
                              accept="image/*"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/upload`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${getAuthToken()}` },
                                    body: formData
                                  });
                                  const data = await response.json();
                                  if (data.url) {
                                    setAnnouncementForm({...announcementForm, image: data.url});
                                    toast.success('Image uploaded!');
                                  }
                                } catch (err) {
                                  toast.error('Failed to upload image');
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="border-zinc-700"
                              onClick={() => announcementImageRef.current?.click()}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                          </div>
                          {announcementForm.image && (
                            <div className="mt-2 relative">
                              <img 
                                src={announcementForm.image} 
                                alt="Preview" 
                                className="w-full h-32 object-cover rounded-lg"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute top-1 right-1 h-6 w-6 bg-black/50 hover:bg-black/70"
                                onClick={() => setAnnouncementForm({...announcementForm, image: ''})}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Display Order</Label>
                          <Input
                            type="number"
                            value={announcementForm.order}
                            onChange={(e) => setAnnouncementForm({...announcementForm, order: e.target.value})}
                            placeholder="0"
                            className="bg-[#1C1C22] border-zinc-700 text-white"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="text-zinc-300">Active</Label>
                          <Switch
                            checked={announcementForm.is_active}
                            onCheckedChange={(checked) => setAnnouncementForm({...announcementForm, is_active: checked})}
                          />
                        </div>
                        <Button type="submit" className="w-full bg-[#FFC107] text-black hover:bg-[#F59E0B]">
                          {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <p className="text-zinc-400 mb-4">
                    Announcements are displayed as a banner on the homepage. Create important notices, promotions, or updates for your customers.
                  </p>
                  {announcements.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No announcements yet. Create your first one!</p>
                  ) : (
                    <div className="space-y-3">
                      {announcements.map((announcement) => (
                        <div 
                          key={announcement.id} 
                          className={`p-4 rounded-lg border ${
                            announcement.is_active 
                              ? 'border-zinc-700 bg-[#1C1C22]/50' 
                              : 'border-[#27272A] bg-[#141417] opacity-60'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={`${
                                  announcement.type === 'info' ? 'bg-blue-500/20 text-blue-400' :
                                  announcement.type === 'success' ? 'bg-green-500/20 text-green-400' :
                                  announcement.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-[#FFC107]/20 text-[#FFC107]'
                                }`}>
                                  {announcement.type.toUpperCase()}
                                </Badge>
                                {!announcement.is_active && (
                                  <Badge variant="outline" className="text-zinc-500 border-zinc-600">INACTIVE</Badge>
                                )}
                                <span className="text-zinc-500 text-sm">Order: {announcement.order}</span>
                              </div>
                              <h4 className="text-white font-semibold">{announcement.title}</h4>
                              <p className="text-zinc-400 text-sm mt-1">{announcement.message}</p>
                              {announcement.image && (
                                <img 
                                  src={announcement.image}
                                  alt="Attachment"
                                  className="mt-2 h-20 w-32 object-cover rounded border border-zinc-700"
                                  data-testid={`admin-announcement-image-${announcement.id}`}
                                />
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-zinc-400 hover:text-white"
                                onClick={() => openEditAnnouncementDialog(announcement)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:text-red-300"
                                onClick={() => deleteAnnouncement(announcement.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default AnnouncementsTab;
